import { writable, derived } from "svelte/store";

interface AuthState {
	accessToken: string | null;
	userId: string | null;
	email: string | null;
	/** Access-token expiry as Unix epoch ms (from the JWT `exp` claim), or null. */
	expiresAt: number | null;
	/** True when the active session is a demo session (JWT `isDemo` claim). */
	isDemo: boolean;
}

function parseJwtPayload(token: string): { sub: string; email: string; exp?: number; isDemo?: boolean } | null {
	try {
		const payload = token.split(".")[1];
		return JSON.parse(atob(payload));
	} catch {
		return null;
	}
}

const EMPTY: AuthState = { accessToken: null, userId: null, email: null, expiresAt: null, isDemo: false };

function createAuthStore() {
	const { subscribe, set } = writable<AuthState>({ ...EMPTY });

	return {
		subscribe,

		setTokens(accessToken: string) {
			const payload = parseJwtPayload(accessToken);
			set({
				accessToken,
				userId: payload?.sub ?? null,
				email: payload?.email ?? null,
				expiresAt: typeof payload?.exp === "number" ? payload.exp * 1000 : null,
				isDemo: payload?.isDemo === true,
			});
		},

		logout() {
			set({ ...EMPTY });
		},
	};
}

export const auth = createAuthStore();

/**
 * True when there is a token but it is expired or within `skewMs` of expiring.
 * Used by the API layer to refresh proactively (before firing a request that
 * would otherwise 401) instead of paying a 401 → refresh → retry round trip.
 */
export function isAccessTokenExpiring(state: AuthState, skewMs = 30_000): boolean {
	if (!state.accessToken) return false;
	if (state.expiresAt === null) return false; // unknown expiry — let the 401 path handle it
	return Date.now() >= state.expiresAt - skewMs;
}

export const isAuthenticated = derived(auth, ($auth) => !!$auth.accessToken);
export const currentUser = derived(auth, ($auth) => ({ userId: $auth.userId, email: $auth.email }));
/** True while the user is in a demo session — drives the app-wide demo banner. */
export const isDemoSession = derived(auth, ($auth) => $auth.isDemo);
